import { useState, useMemo } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { TrainingTopic } from '../../types/trainingTypes';

interface AssignToPlansModalProps {
    isOpen: boolean;
    onClose: () => void;
    topic: TrainingTopic;
}

export const AssignToPlansModal = ({ isOpen, onClose, topic }: AssignToPlansModalProps) => {
    const { plans, updateTopic } = useSafetyTrainingContext();
    const eligiblePlans = useMemo(
        () => plans.filter(p => p.status === 'DRAFT' || p.status === 'ACTIVE'),
        [plans]
    );

    const [selectedPlanIds, setSelectedPlanIds] = useState<Set<string>>(
        new Set(topic.planIds || [])
    );
    const [saving, setSaving] = useState(false);

    if (!isOpen) return null;

    const allSelected = eligiblePlans.length > 0 && eligiblePlans.every(p => selectedPlanIds.has(p.id));

    const togglePlan = (planId: string) => {
        setSelectedPlanIds(prev => {
            const next = new Set(prev);
            if (next.has(planId)) next.delete(planId);
            else next.add(planId);
            return next;
        });
    };

    const toggleAll = () => {
        if (allSelected) {
            setSelectedPlanIds(new Set());
        } else {
            setSelectedPlanIds(new Set(eligiblePlans.map(p => p.id)));
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateTopic({
                id: topic.id,
                planIds: Array.from(selectedPlanIds),
            } as any);
            onClose();
        } catch (err) {
            console.error('Error assigning topic to plans:', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="cr-modal-overlay" onClick={onClose}>
            <div className="cr-modal" onClick={e => e.stopPropagation()}>
                <div className="cr-modal-header">
                    <h2 className="cr-modal-title">Assign to Plans</h2>
                    <button className="cr-modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="cr-modal-body">
                    <p style={{ fontSize: '0.85rem', color: '#374151', marginBottom: 16 }}>
                        Select which training plans "<strong>{topic.description}</strong>" should be available in.
                    </p>

                    {eligiblePlans.length === 0 ? (
                        <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                            No draft or active plans available. Create a plan first.
                        </p>
                    ) : (
                        <>
                            <label className="cr-checkbox-label" style={{ marginBottom: 12, display: 'flex', fontWeight: 600 }}>
                                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                                All Plans ({eligiblePlans.length})
                            </label>
                            <div style={{ borderTop: '1px solid #e8ecef', paddingTop: 8 }}>
                                {eligiblePlans.map(plan => (
                                    <label key={plan.id} className="cr-checkbox-label" style={{ display: 'flex', marginBottom: 8 }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedPlanIds.has(plan.id)}
                                            onChange={() => togglePlan(plan.id)}
                                        />
                                        <span>
                                            {plan.name}
                                            <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#6b7280' }}>
                                                ({plan.status})
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <div className="cr-modal-footer">
                    <button className="cr-btn cr-btn--secondary" onClick={onClose}>Cancel</button>
                    <button className="cr-btn cr-btn--primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};
